import { Dispatch, SetStateAction, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

export const PAGE_NUMBER_SEARCH_PARAM = "page";

/**
 * Reads a page number from the query string, falling back to the first page for anything unusable.
 *
 * @param value raw `page` query value
 * @returns a page number of 1 or more
 * @example
 * parsePageNumber("3"); // 3
 * parsePageNumber("abc"); // 1
 */
export const parsePageNumber = (value: string | null): number => {
  const pageNumber = Number(value);
  return Number.isInteger(pageNumber) && pageNumber >= 1 ? pageNumber : 1;
};

/**
 * Keeps the current page of a paginated list in the `page` query parameter, so a reload stays on
 * the same page and the browser back button returns to the page before.
 *
 * Moving to another page pushes a history entry. The first page is written without the parameter so
 * the list keeps its plain URL. Other query parameters are left as they are.
 *
 * @returns the current page number and a setter shaped like `useState`'s
 * @example
 * const [currentPageNumber, setCurrentPageNumber] = usePageNumberSearchParam();
 */
const usePageNumberSearchParam = (): [number, Dispatch<SetStateAction<number>>] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPageNumber = parsePageNumber(searchParams.get(PAGE_NUMBER_SEARCH_PARAM));

  const setCurrentPageNumber = useCallback<Dispatch<SetStateAction<number>>>(
    (action) => {
      const nextPageNumber = typeof action === "function" ? action(currentPageNumber) : action;
      // Navigating to the same page would push a duplicate history entry.
      if (nextPageNumber === currentPageNumber) return;

      const nextSearchParams = new URLSearchParams(searchParams);
      if (nextPageNumber <= 1) {
        nextSearchParams.delete(PAGE_NUMBER_SEARCH_PARAM);
      } else {
        nextSearchParams.set(PAGE_NUMBER_SEARCH_PARAM, String(nextPageNumber));
      }
      setSearchParams(nextSearchParams);
    },
    [currentPageNumber, searchParams, setSearchParams]
  );

  return [currentPageNumber, setCurrentPageNumber];
};

export default usePageNumberSearchParam;
